import Select from "react-select";
import { FormEvent, useEffect, useState } from "react";
import { Camera, Check, UploadCloud, UserRound } from "lucide-react";
import { Link } from "react-router-dom";
import { Modal } from "../../components/shared/controls/Modal";
import logo from "../../assets/images/knewlogow.png";
import { applyAsAgent } from "@services/krent.service";
import "./AgentForm.css";

export default function AgentForm() {
  const [idType, setIdType] = useState<string>("");
  const [photo, setPhoto] = useState<File | null>(null);
  const [preview, setPreview] = useState("");
  const [idDocument, setIdDocument] = useState<File | null>(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!photo) {
      setPreview("");
      return;
    }
    const url = URL.createObjectURL(photo);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [photo]);

  const selectFile = (file: File | undefined, isPhoto: boolean) => {
    if (!file) return;
    const valid = isPhoto
      ? ["image/jpeg", "image/png"].includes(file.type)
      : ["image/jpeg", "image/png", "application/pdf"].includes(file.type);
    if (!valid || file.size > 25 * 1024 * 1024) {
      setError(
        "Choose a JPG or PNG" +
          (isPhoto ? "" : " or PDF") +
          " file up to 25 MB.",
      );
      return;
    }
    setError("");
    if (isPhoto) setPhoto(file);
    else setIdDocument(file);
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (loading) return;
    const formElement = event.currentTarget;
    if (!idType) { setError("Please select your means of ID."); return; }
    if (!photo) {
      setError("Please upload your profile photo.");
      return;
    }
    if (!idDocument) {
      setError("Please upload your means of ID.");
      return;
    }

    const fd = new FormData(event.currentTarget);
    const accountNumber = (fd.get("accountNumber") as string) ?? "";
    const accountName = (fd.get("accountName") as string) ?? "";
    const bankName = (fd.get("bankName") as string) ?? "";

    setError("");
    setLoading(true);
    try {
      const result = await applyAsAgent({
        name: (fd.get("fullName") as string) ?? "",
        email: (fd.get("email") as string) ?? "",
        phone: (fd.get("phone") as string) ?? "",
        agencyName: (fd.get("agencyName") as string) ?? "",
        whatsapp: (fd.get("whatsapp") as string) ?? "",
        location: (fd.get("location") as string) || undefined,
        idType: (fd.get("idType") as string) ?? "",
        bankDetails: { accountNumber, accountName, bankName },
        photo,
        idDocument,
        note: (fd.get("note") as string) || undefined,
      });
      formElement.reset();
      setIdType("");
      setPhoto(null);
      setIdDocument(null);
      setSuccessMessage(result?.message || "");
      setSuccess(true);
    } catch (err: any) {
      setError(
        err?.message ?? "Failed to submit application. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="agent-sign-on font-onest">
      <header className="agent-sign-on__header">
        <div>Agent Form</div>
      </header>
      <main className="agent-sign-on__main">
        <div className="agent-sign-on__banner">
          <h1>Agent Sign On Form</h1>
        </div>
        <h2>Agent Details</h2>
        <p className="agent-sign-on__intro">
          Enter your information below; ensure all the information provided is
          accurate
        </p>
        <form onSubmit={submit}>
          <fieldset disabled={loading}>
          {/* Photo upload */}
          <div className="agent-sign-on__photo-row">
            <div className="agent-sign-on__avatar">
              {preview ? (
                <img src={preview} alt="Agent preview" />
              ) : (
                <UserRound size={60} strokeWidth={1.5} />
              )}
            </div>
            <label className="agent-sign-on__photo-upload">
              <Camera size={18} />
              <span>Upload Image</span>
              <input
                aria-label="Upload agent image"
                type="file"
                accept="image/jpeg,image/png"
                onChange={(e) => selectFile(e.target.files?.[0], true)}
              />
            </label>
          </div>

          <div className="agent-sign-on__grid">
            <label>
              Full name
              <input
                name="fullName"
                autoComplete="name"
                required
                placeholder="Full name"
              />
            </label>
            <label>
              Agency name
              <input
                name="agencyName"
                required
                placeholder="e.g. Bakare Properties"
              />
            </label>
            <label>
              Email
              <input
                name="email"
                autoComplete="email"
                type="email"
                required
                placeholder="you@example.com"
              />
            </label>
            <label>
              Phone number
              <input
                name="phone"
                autoComplete="tel"
                type="tel"
                required
                placeholder="Phone number"
                pattern="[+0-9 ()-]{7,20}"
              />
            </label>
            <label>
              Whatsapp number
              <input
                name="whatsapp"
                type="tel"
                required
                placeholder="Whatsapp number"
                pattern="[+0-9 ()-]{7,20}"
              />
            </label>
            <label>
              Bank Name
              <input name="bankName" required placeholder="Enter bank name" />
            </label>
            <label>
              Account number
              <input
                name="accountNumber"
                inputMode="numeric"
                pattern="[0-9]{10}"
                maxLength={10}
                required
                placeholder="Enter your 10 digit number"
              />
            </label>
            <label>
              Account name
              <input name="accountName" required placeholder="Account name" />
            </label>
            <label>
              Location/Area (Optional)
              <input name="location" placeholder="e.g. Ajah, Lagos" />
            </label>
            <label>
              Means of ID
              <Select
                name="idType"
                inputId="agent-id-type"
                aria-label="Means of ID"
                placeholder="Select means of ID"
                isSearchable={false}
                isDisabled={loading}
                value={idType ? { value: idType, label: idType } : null}
                onChange={option => setIdType(option?.value || "")}
                options={["NIN", "International Passport", "Driver's License", "Voter's Card"].map(value => ({ value, label: value }))}
                classNamePrefix="agent-id-select"
                menuPortalTarget={document.body}
                styles={{
                  control: (base, state) => Object.assign({}, base, { minHeight: 48, borderRadius: 28, borderColor: state.isFocused ? "#47bba9" : "#d0d5dd", boxShadow: state.isFocused ? "0 0 0 3px #47bba920" : "none", padding: "0 12px" }),
                  menu: base => Object.assign({}, base, { borderRadius: 16, padding: 6, boxShadow: "0 10px 30px #10182820" }),
                  option: (base, state) => Object.assign({}, base, { borderRadius: 10, backgroundColor: state.isSelected ? "#def3ed" : state.isFocused ? "#f0faf7" : "white", color: "#344054", cursor: "pointer" }),
                  menuPortal: base => Object.assign({}, base, { zIndex: 1100 }),
                  indicatorSeparator: () => ({ display: "none" }),
                }}
              />
            </label>
          </div>

          {/* ID document upload */}
          <label className="agent-sign-on__document">
            <UploadCloud size={22} />
            <span>{idDocument?.name || "Tap to upload your ID"}</span>
            <small>PDF, JPG or PNG · up to 25 MB each</small>
            <input
              aria-label="Upload your ID"
              type="file"
              accept="application/pdf,image/jpeg,image/png"
              onChange={(e) => selectFile(e.target.files?.[0], false)}
            />
          </label>

          <label className="agent-sign-on__note">
            Note (optional)
            <textarea
              name="note"
              rows={4}
              placeholder="Enter any other information about this agent"
            />
          </label>

          {error && (
            <p role="alert" className="text-danger">
              {error}
            </p>
          )}

          <div className="agent-sign-on__submit">
            <button type="submit" disabled={loading}>
              {loading ? "Submitting…" : "Submit"}
            </button>
          </div>
          </fieldset>
        </form>
      </main>

      <footer className="agent-sign-on__footer">
        <div>
          <Link to="/">
            <img src={logo} alt="Krent — Own Your Dreams" />
          </Link>
          <nav aria-label="Social media">
            <a
              href="https://www.linkedin.com/company/krent-inc/"
              aria-label="LinkedIn"
            >
              <i className="fa-brands fa-linkedin" />
            </a>
            <a
              href="https://www.instagram.com/krent_inc/"
              aria-label="Instagram"
            >
              <i className="fa-brands fa-instagram" />
            </a>
            <a href="https://www.facebook.com/KrentInc" aria-label="Facebook">
              <i className="fa-brands fa-facebook" />
            </a>
          </nav>
          <p>Follow us on social media, never miss a thing!</p>
          <small>Copyright {new Date().getFullYear()} | Krent</small>
        </div>
      </footer>

      <Modal
        show={success}
        onClose={() => setSuccess(false)}
        size={440}
        className="agent-sign-on-success"
      >
        <div className="agent-sign-on-success__body">
          <div className="agent-sign-on-success__check">
            <Check size={36} color="white" />
          </div>
          <h2>Successful!</h2>
          <p>
            {successMessage ||
              "Thanks. We received your application and will review it shortly. You will hear from us once it is approved."}
          </p>
        </div>
      </Modal>
    </div>
  );
}
